/**
 * Layer scale for the Host Panel, from the approved prototype (handoff §4, §9.1, §10).
 *
 * The classes are **literal** on purpose: Tailwind discovers utilities by scanning
 * source, so a `z-[${value}]` assembled at runtime never reaches the CSS. Keeping the
 * scale in one module is what makes "does the toast cover the calendar?" answerable.
 */

export const PANEL_LAYER_Z = {
  /** Facet rail pinned to the top of the catalog grid. */
  facetRail: 5,
  /** Action bars pinned to the foot of the catalog. */
  footerBar: 40,
  /** Anchored facet layer, on desktop. */
  facetPopover: 60,
  /** Sticky panel header. */
  header: 200,
  /**
   * The cookie notice — **not a panel surface**, and here anyway.
   *
   * It is site-wide (`components/legal/cookie-consent-banner.tsx`), but it shares the stacking
   * context with everything below, and this module exists precisely so "does the notice cover
   * the mode control?" has an answer. It shipped at 335, which put it over `modeFob` (320) and
   * `bulkBar` (329): both sit at the bottom edge, so until the notice was answered the person
   * could not switch between "Minha lista", "Adicionar peças" and "Ver como convidado" at all —
   * Playwright measured 54 refused clicks, the notice intercepting every one.
   *
   * 210 puts it over ordinary content and under every floating surface: drawers, sheets, the mode
   * control, the bulk bar, dialogs and the toast. The decision behind the number (2026-10-14) is
   * that the notice is **not a gate** — it is a yes/no a person may leave for later while using
   * the screen.
   */
  cookieNotice: 210,
  /** Menu drawer. */
  menuDrawer: 250,
  /** Order drawer and presentation drawer. */
  drawer: 300,
  /** Veil behind the facet bottom sheet, on mobile. */
  facetBackdrop: 300,
  /** Facet bottom sheet. */
  facetSheet: 310,
  /** Floating catalog mode control. */
  modeFob: 320,
  /** Bulk selection bar. */
  bulkBar: 329,
  /** Veil behind the quantity and pack dialog. */
  packOverlay: 340,
  /** Card of the quantity and pack dialog. */
  packDialog: 341,
  /** Veil behind the cover's cropper. */
  cropOverlay: 350,
  /** Card of the cover's cropper. */
  cropDialog: 351,
  /** Birth date calendar dialog. */
  calendar: 420,
  /** Toast: covers everything it confirms. */
  toast: 430,
  /** Full-screen loader, the top of the stack. */
  pageLoader: 900,
} as const;

export type PanelLayer = keyof typeof PANEL_LAYER_Z;

export const PANEL_LAYER_CLASS: Record<PanelLayer, string> = {
  facetRail: "z-[5]",
  footerBar: "z-[40]",
  facetPopover: "z-[60]",
  header: "z-[200]",
  cookieNotice: "z-[210]",
  menuDrawer: "z-[250]",
  drawer: "z-[300]",
  facetBackdrop: "z-[300]",
  facetSheet: "z-[310]",
  modeFob: "z-[320]",
  bulkBar: "z-[329]",
  packOverlay: "z-[340]",
  packDialog: "z-[341]",
  cropOverlay: "z-[350]",
  cropDialog: "z-[351]",
  calendar: "z-[420]",
  toast: "z-[430]",
  pageLoader: "z-[900]",
};
